import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { versioned } from "@/lib/asset-version";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "MedicalOrganization",
  "@id": "https://healic.in/#organization",
  name: "Healic Multispeciality Hospital",
  url: "https://healic.in",
  logo: "https://healic.in/favicon.png",
  image: "https://healic.in/og-image.jpg",
  slogan: "Tension-free planned surgery. Fixed prices. No billing surprises.",
  telephone: "+91-95887-95887",
  email: "info@healic.in",
  areaServed: ["Gurugram", "Ghaziabad", "Haridwar", "Agra"],
  medicalSpecialty: [
    "http://schema.org/Surgical",
    "http://schema.org/Obstetric",
    "http://schema.org/Gynecologic",
    "http://schema.org/Musculoskeletal",
    "http://schema.org/Urologic",
    "http://schema.org/Otolaryngologic",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "68 Avenue, Sec-68, Badshahpur, Sohna Road",
    addressLocality: "Gurugram",
    addressRegion: "Haryana",
    postalCode: "122101",
    addressCountry: "IN",
  },
  sameAs: [
    "https://www.instagram.com/healichospital",
    "https://in.linkedin.com/company/healic-health",
    "https://facebook.com",
    "https://youtube.com",
  ],
  location: [
    {
      "@type": "Hospital",
      name: "Healic — Sohna Road",
      address: {
        "@type": "PostalAddress",
        streetAddress: "68 Avenue, Sec-68, Badshahpur, Sohna Road",
        addressLocality: "Gurugram",
        addressRegion: "Haryana",
        postalCode: "122101",
        addressCountry: "IN",
      },
    },
    {
      "@type": "Hospital",
      name: "Healic — Indirapuram",
      address: {
        "@type": "PostalAddress",
        streetAddress: "C-3, Saya Zenith, Ahinsa Khand 2, Indirapuram",
        addressLocality: "Ghaziabad",
        addressRegion: "Uttar Pradesh",
        postalCode: "201014",
        addressCountry: "IN",
      },
    },
    {
      "@type": "Hospital",
      name: "Healic — Haridwar",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Ranipur More, Haridwar Main Rd",
        addressLocality: "Haridwar",
        addressRegion: "Uttarakhand",
        postalCode: "249401",
        addressCountry: "IN",
      },
    },
    {
      "@type": "Hospital",
      name: "Healic — Agra",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Plot No-1 A, Bodla Rd, Shahganj",
        addressLocality: "Agra",
        addressRegion: "Uttar Pradesh",
        postalCode: "282012",
        addressCountry: "IN",
      },
    },
  ],
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Healic Multispeciality Hospital",
  alternateName: ["Healic", "Healic Hospital", "Healic Clinics"],
  url: "https://healic.in",
  publisher: {
    "@type": "MedicalOrganization",
    name: "Healic Multispeciality Hospital",
    url: "https://healic.in",
    logo: "https://healic.in/favicon.png",
  },
};

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-pill bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-pill bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Try again
          </button>
          <a href="/" className="rounded-pill border border-border bg-background px-5 py-2.5 text-sm font-medium hover:bg-muted">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Healic Hospital — Fixed-Price Planned Surgery" },
      { name: "description", content: "Healic offers tension-free planned surgery with fixed prices and no billing surprises across Gurugram, Ghaziabad, Haridwar and Agra." },
      { name: "theme-color", content: "#1B1F4A" },
      { name: "google-site-verification", content: "3qHVuHmc5ymubCWr_uljBNb_fAzNxveMhMCo-pHXi2w" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Healic Multispeciality Hospital" },
      { property: "og:title", content: "Healic Hospital — Fixed-Price Planned Surgery" },
      { property: "og:description", content: "Healic offers tension-free planned surgery with fixed prices and no billing surprises across Gurugram, Ghaziabad, Haridwar and Agra." },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Healic Hospital — Fixed-Price Planned Surgery" },
      { name: "twitter:description", content: "Healic offers tension-free planned surgery with fixed prices and no billing surprises across Gurugram, Ghaziabad, Haridwar and Agra." },
      { property: "og:image", content: `https://healic.in${versioned("/og-image.jpg")}` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Healic Multispeciality Hospital — planned surgery with fixed prices" },
      { name: "twitter:image", content: `https://healic.in${versioned("/og-image.jpg")}` },
      { name: "twitter:image:alt", content: "Healic Multispeciality Hospital — planned surgery with fixed prices" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: versioned("/favicon.ico") },
      { rel: "icon", type: "image/png", sizes: "48x48", href: versioned("/favicon-48x48.png") },
      { rel: "icon", type: "image/png", sizes: "96x96", href: versioned("/favicon-96x96.png") },
      { rel: "icon", type: "image/png", sizes: "192x192", href: versioned("/favicon-192x192.png") },
      { rel: "icon", type: "image/png", sizes: "512x512", href: versioned("/favicon.png") },
      { rel: "apple-touch-icon", sizes: "180x180", href: versioned("/apple-touch-icon.png") },
      { rel: "preconnect", href: "https://healic.in" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(orgJsonLd),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(websiteJsonLd),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster />
    </QueryClientProvider>
  );
}
